import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { ScoreGauge } from "../components/ScoreGauge";
import { api } from "../lib/api";
import { Loader2 } from "lucide-react";

export default function Results() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/submissions/${id}`);
                setData(data);
            } catch (e) {
                setErr(e.response?.data?.detail || "Could not load results");
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    if (loading) {
        return (
            <Layout>
                <div className="max-w-3xl mx-auto px-6 py-24 flex items-center gap-3 text-ash-gray-dark">
                    <Loader2 className="animate-spin" />
                    Preparing your results…
                </div>
            </Layout>
        );
    }
    if (err || !data) {
        return (
            <Layout>
                <div className="max-w-3xl mx-auto px-6 py-24" data-testid="results-error">
                    <h1 className="font-display font-bold text-3xl">We couldn't load your results.</h1>
                    <p className="mt-3 text-ash-gray-dark">{err}</p>
                    <Link to="/" className="btn-primary mt-6 inline-flex">Back home</Link>
                </div>
            </Layout>
        );
    }

    const ir = data.instant_result || {};
    const recs = ir.recommendations || [];

    return (
        <Layout minimal>
            <section className="bg-ash-off-white" data-testid="results-section">
                <div className="max-w-5xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-10 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7 fade-in">
                        <div className="text-xs uppercase tracking-widest font-bold text-ash-orange mb-4" data-testid="results-eyebrow">
                            Your Marketing Clarity Checkup result
                        </div>
                        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-ash-black leading-[0.95]">
                            Your Marketing Profile:
                            <br />
                            <span className="text-ash-orange" data-testid="results-profile-name">{data.profile_name}</span>
                        </h1>
                        <p className="mt-6 text-lg text-ash-gray-dark max-w-xl leading-relaxed" data-testid="results-summary">
                            {ir.summary ||
                                `Based on your answers, your organization aligns most with the ${data.profile_name} profile.`}
                        </p>
                    </div>
                    <div className="md:col-span-5 flex flex-col items-center">
                        <ScoreGauge score={data.overall_score} />
                        <div className="mt-2 font-bold text-ash-black" data-testid="results-score-label">
                            Marketing Clarity Score
                        </div>
                    </div>
                </div>
            </section>

            {/* Category breakdown */}
            <section className="bg-white border-t border-ash-gray-light" data-testid="results-category-section">
                <div className="max-w-5xl mx-auto px-6 md:px-10 py-12">
                    <div className="text-xs uppercase tracking-widest font-bold text-ash-gray-dark mb-3">Score breakdown</div>
                    <h2 className="font-display font-bold text-2xl sm:text-3xl text-ash-black mb-8">
                        How things look across six areas
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-x-10 gap-y-5">
                        {Object.entries(data.category_labels).map(([k, label]) => {
                            const v = data.category_scores[k] || 0;
                            return (
                                <div key={k} data-testid={`category-row-${k}`}>
                                    <div className="flex justify-between items-baseline mb-2">
                                        <span className="font-bold text-ash-black">{label}</span>
                                        <span className="font-display font-black text-ash-orange">{v}<span className="text-ash-gray-dark text-sm font-normal">/100</span></span>
                                    </div>
                                    <div className="h-1.5 w-full bg-ash-gray-light">
                                        <div className="h-full bg-ash-orange" style={{ width: `${v}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Recommendations */}
            <section className="bg-ash-off-white" data-testid="results-recs-section">
                <div className="max-w-5xl mx-auto px-6 md:px-10 py-14">
                    <div className="text-xs uppercase tracking-widest font-bold text-ash-orange mb-3">A few things our team would consider</div>
                    <h2 className="font-display font-bold text-2xl sm:text-3xl text-ash-black mb-8">Practical next steps</h2>
                    <div className="grid md:grid-cols-3 gap-5">
                        {recs.length > 0 ? recs.slice(0, 3).map((r, i) => (
                            <div key={i} className="bg-white border border-ash-gray-light p-6" data-testid={`results-rec-${i}`}>
                                <div className="font-display font-black text-3xl text-ash-orange leading-none">0{i + 1}</div>
                                <p className="mt-4 text-ash-gray-dark leading-relaxed">{r}</p>
                            </div>
                        )) : (
                            <div className="md:col-span-3 text-ash-gray-dark">
                                Recommendations are being prepared. Please refresh in a moment.
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-ash-black text-white" data-testid="results-cta-section">
                <div className="max-w-5xl mx-auto px-6 md:px-10 py-14">
                    <div className="text-xs uppercase tracking-widest font-bold text-ash-orange mb-3">Want a deeper look?</div>
                    <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">
                        {ir.next_step ||
                            "Our team can review your answers and prepare a more detailed Marketing Clarity Report with additional recommendations."}
                    </h2>
                    {data.requested_report && (
                        <div className="mt-6 inline-block bg-ash-orange text-white px-4 py-2 text-sm font-bold" data-testid="results-report-requested">
                            Your detailed report is on its way — our team will review and email it shortly.
                        </div>
                    )}
                </div>
            </section>
        </Layout>
    );
}
